/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
// eslint-disable-next-line jira/import-whitelist
import { JIRA_PROJECT_TYPE_SERVICE_DESK_PROJECT } from '@jira/platform__project-types/src/project-types.tsx';
import { checkLicense } from '@jira/spa-apps__interceptors/src/checkLicense.tsx';
// eslint-disable-next-line jira/import-whitelist
import {
	INCIDENT_MANAGEMENT_FEATURE,
	SERVICE_REQUEST_FEATURE,
} from '@jira/servicedesk__common/src/Enabled.tsx';
import {
	createResource,
	type RouterDataContext,
	type ResourceStoreContext,
	type RouteResource,
} from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';

import { performGetRequest } from '@atlassian/jira-fetch';
import {
	RESOURCE_TYPE_AI_CONTEXT_PANEL,
	RESOURCE_TYPE_AI_CONTEXT_PANEL_WITH_DEPENDENCIES,
	RESOURCE_TYPE_CONTEXT_PANEL_ISSUE_DETAILS,
} from './resourceTypeContextPanelIssueDetails.constants.tsx';
import { getDependencyData } from './getDependencyData.tsx';
import type { AIContextServicePanelResourceResponse } from './ContextPanelServiceTypes.tsx';
import { getServiceSuggestionData } from './getServiceSuggestionData.tsx';
import { getIncidentSuggestionEndpoint } from './getIncidentSuggestionEndpoint.tsx';
import { contextPanelIssueDetailsResource, getIssueDetails } from './getIssueDetails.tsx';
import type { IncidentSuggestion } from './IncidentSuggestion.types.tsx';
import type { IssueDetails } from './IssueDetails.types.tsx';

const SUPPORTED_PRACTICES = new Set<string | null | undefined>([
	SERVICE_REQUEST_FEATURE,
	INCIDENT_MANAGEMENT_FEATURE,
]);

// We use 1/0 to reduce characters as user preferences has a 255 limit
export type UserPreferenceResult = {
	1: string[]; // expanded group ids
	0: string[]; // collapsed group ids
};

export const EMPTY_USER_PREFERENCE: UserPreferenceResult = { 0: [], 1: [] };

export function getUserPreferenceFromResult(userPreferences: UserPreferenceResult): {
	isContextPanelCollapsed: boolean;
	emptyUserPreference: boolean;
} {
	const collapsed = userPreferences[0] ?? [];
	const expanded = userPreferences[1] ?? [];
	const isContextPanelCollapsed = collapsed.includes('ai-context-service');
	const emptyUserPreference = !(
		collapsed.includes('ai-context-service') || expanded.includes('ai-context-service')
	);
	return { isContextPanelCollapsed, emptyUserPreference };
}

export const aiContextPanelResource: RouteResource<
	AIContextServicePanelResourceResponse | IncidentSuggestion | null
> = createResource<AIContextServicePanelResourceResponse | IncidentSuggestion | null>({
	type: RESOURCE_TYPE_AI_CONTEXT_PANEL,
	getKey: ({ match }) => {
		const issueKey = match.params.issueKey || '';
		return `aiContextPanelResource-${issueKey}`;
	},
	getData: async (
		routerDataContext: RouterDataContext,
		resourceStoreContext: ResourceStoreContext,
	) => {
		const { skipChecks, issueKey } = routerDataContext.match.params;
		let { issueId, practice, projectId } = routerDataContext.match.params;
		const { cloudId, locale, appEditions } = resourceStoreContext.tenantContext;
		const isStandardEdition = appEditions.serviceDesk === 'STANDARD_EDITION';
		const isPremiumEdition = appEditions.serviceDesk === 'PREMIUM_EDITION';

		if (!issueKey) {
			return null;
		}
		if (skipChecks !== '1') {
			// The suggestions panel is available only on service-desk.
			// Hence filtering them out to reduce the number of calls to AGG and features APIs
			if (!checkLicense(resourceStoreContext.tenantContext, 'servicedesk')) {
				return null;
			}
			const issueDetails = await getIssueDetails(issueKey, cloudId, true);
			const { isAIEnabled, isAiContextFeatureEnabled, projectType } = issueDetails;
			issueId = issueDetails.issueId;
			projectId = issueDetails.projectId;
			practice = issueDetails.practice;
			const projectKeyFromIssueDetails = issueDetails.projectKey;
			// Check AI enabled and supported practices (or null practice with feature flag)
			if (
				!isAIEnabled ||
				(!SUPPORTED_PRACTICES.has(practice) &&
					!(practice === undefined && fg('suggestions_panel_null_with_nsynk'))) ||
				projectType !== JIRA_PROJECT_TYPE_SERVICE_DESK_PROJECT
			) {
				return null;
			}
			/* (Standard or Premium) + Collapsed Panel -> Skip Context Panel Call */
			const reactivePremiumEnabled =
				isPremiumEdition && fg('enable_reactive_suggestions_for_premium');
			const reactiveStandardEnabled =
				isStandardEdition && fg('collapsed_context_panel_for_standard');
			if (
				(reactiveStandardEnabled || reactivePremiumEnabled) &&
				practice === SERVICE_REQUEST_FEATURE
			) {
				let userPreferences: UserPreferenceResult;
				try {
					userPreferences = await performGetRequest<UserPreferenceResult>(
						`/rest/api/3/mypreferences?key=issue.collapsible-group.${projectKeyFromIssueDetails}`,
					);
				} catch {
					userPreferences = EMPTY_USER_PREFERENCE;
				}
				const { isContextPanelCollapsed, emptyUserPreference } =
					getUserPreferenceFromResult(userPreferences);
				/* Panel Collapsed or enabled first time and feature-toggle is OFF */
				if (isContextPanelCollapsed || (emptyUserPreference && !isAiContextFeatureEnabled))
					return null;
			} else if (!isAiContextFeatureEnabled) {
				return null;
			}
		}
		if (!issueId) {
			throw new Error('AI context service panel resource cannot be loaded without an issueId');
		}
		if (
			practice === SERVICE_REQUEST_FEATURE ||
			(practice === undefined && fg('suggestions_panel_null_with_nsynk'))
		) {
			return getServiceSuggestionData(issueId, cloudId);
		}
		if (practice === INCIDENT_MANAGEMENT_FEATURE && fg('jsm_issue_view_ai_context_ops')) {
			if (!projectId) {
				throw new Error('AI context ops panel resource cannot be loaded without a projectId');
			}
			return getIncidentSuggestionEndpoint({
				cloudId,
				issueId,
				projectId,
				locale,
			});
		}
		return null;
	},
	maxAge: 0,
	isBrowserOnly: true,
});

export const aiContextPanelResourceWithDependencies: RouteResource<
	AIContextServicePanelResourceResponse | IncidentSuggestion | null
> = // eslint-disable-next-line jira/router/no-daisy-chaining-resources
	createResource<AIContextServicePanelResourceResponse | IncidentSuggestion | null>({
		type: RESOURCE_TYPE_AI_CONTEXT_PANEL_WITH_DEPENDENCIES,
		getKey: ({ match }) => {
			const issueKey = match.params.issueKey || '';
			return `aiContextPanelResourceWithDependencies-${issueKey}`;
		},
		getData: async (
			routerDataContext: RouterDataContext,
			resourceStoreContext: ResourceStoreContext,
		) => {
			const { skipChecks, issueKey } = routerDataContext.match.params;
			let { issueId, practice, projectId } = routerDataContext.match.params;
			const { cloudId, locale, appEditions } = resourceStoreContext.tenantContext;
			const isStandardEdition = appEditions.serviceDesk === 'STANDARD_EDITION';
			const isPremiumEdition = appEditions.serviceDesk === 'PREMIUM_EDITION';

			if (!issueKey) {
				return null;
			}
			if (skipChecks !== '1') {
				if (!checkLicense(resourceStoreContext.tenantContext, 'servicedesk')) {
					return null;
				}
				let issueDetails = getDependencyData<IssueDetails | null>(
					routerDataContext.dependencies,
					RESOURCE_TYPE_CONTEXT_PANEL_ISSUE_DETAILS,
				);
				if (issueDetails instanceof Promise) {
					issueDetails = await issueDetails;
				}
				if (!issueDetails) {
					return null;
				}
				const { isAIEnabled, isAiContextFeatureEnabled, projectType } = issueDetails;
				issueId = issueDetails.issueId;
				projectId = issueDetails.projectId;
				practice = issueDetails.practice;
				const projectKeyFromIssueDetails = issueDetails.projectKey;

				if (
					!isAIEnabled ||
					(!SUPPORTED_PRACTICES.has(practice) &&
						!(practice === undefined && fg('suggestions_panel_null_with_nsynk'))) ||
					projectType !== JIRA_PROJECT_TYPE_SERVICE_DESK_PROJECT
				) {
					return null;
				}
				/* (Standard OR Premium) + Collapsed Panel -> Skip Context Panel Call */
				const reactivePremiumEnabled =
					isPremiumEdition && fg('enable_reactive_suggestions_for_premium');
				const reactiveStandardEnabled =
					isStandardEdition && fg('collapsed_context_panel_for_standard');
				if (
					(reactiveStandardEnabled || reactivePremiumEnabled) &&
					practice === SERVICE_REQUEST_FEATURE
				) {
					let userPreferenceResult: UserPreferenceResult;
					try {
						userPreferenceResult = await performGetRequest<UserPreferenceResult>(
							`/rest/api/3/mypreferences?key=issue.collapsible-group.${projectKeyFromIssueDetails}`,
						);
					} catch {
						userPreferenceResult = EMPTY_USER_PREFERENCE;
					}
					const { isContextPanelCollapsed, emptyUserPreference } =
						getUserPreferenceFromResult(userPreferenceResult);

					/* Panel Collapsed or enabled first time and feature-toggle is OFF - skip making API call */
					if (isContextPanelCollapsed || (emptyUserPreference && !isAiContextFeatureEnabled))
						return null;
				} else if (!isAiContextFeatureEnabled) {
					return null;
				}
			}
			if (!issueId) {
				throw new Error('AI context service panel resource cannot be loaded without an issueId');
			}
			if (
				practice === SERVICE_REQUEST_FEATURE ||
				(practice === undefined && fg('suggestions_panel_null_with_nsynk'))
			) {
				return getServiceSuggestionData(issueId, cloudId);
			}
			if (practice === INCIDENT_MANAGEMENT_FEATURE && fg('jsm_issue_view_ai_context_ops')) {
				if (!projectId) {
					throw new Error('AI context ops panel resource cannot be loaded without a projectId');
				}
				return getIncidentSuggestionEndpoint({
					cloudId,
					issueId,
					projectId,
					locale,
				});
			}
			return null;
		},
		maxAge: 0,
		depends: [RESOURCE_TYPE_CONTEXT_PANEL_ISSUE_DETAILS],
		isBrowserOnly: true,
	});

export const getAIContextPanelResources = (): (
	| RouteResource<AIContextServicePanelResourceResponse | IncidentSuggestion | null>
	| RouteResource<IssueDetails | null>
)[] => {
	return [contextPanelIssueDetailsResource, aiContextPanelResourceWithDependencies];
};
